import { computed, type ComputedRef } from 'vue'
import { useRoute, useRouter } from 'vue-router'

export interface UrlStateSerDe<T> {
  /**
   * `query` может содержать значения из cached defaults (Date, number, и т.п.) merged
   * со строками из URL. SerDe должен корректно обрабатывать heterogeneous типы.
   * Array-параметры (`?tag=a&tag=b` → `string[]`) проходят как есть — SerDe должен
   * их обработать или гарантировать, что такие ключи не используются.
   */
  parse: (query: Record<string, unknown>) => T
  /** `currentQuery` отражает только URL state; defaults в нём не merged. */
  serialize: (
    value: Partial<T>,
    currentQuery: Record<string, string | undefined>,
  ) => Record<string, string | undefined>
  defaults?: () => Partial<T>
  /**
   * Глобально уникальный статический литерал. Consumer'ы с одним cacheKey шарят
   * Date instances (это by design). Коллизии разных типов под одним ключом
   * приводят к silent shape mismatch — TypeScript их не ловит.
   */
  cacheKey: string
  /**
   * Если true — `defaults()` вызывается на каждом recompute, минуя cache.
   * Использовать для wall-clock-зависимых defaults (since/until от Date.now())
   * чтобы избежать stale window после долгой сессии. Trade-off: новый Date instance
   * при каждом recompute (но `parse` имеет шанс coerce'ить через `instanceof Date`).
   */
  volatileDefaults?: boolean
}

const _defaultsCaches = new Map<string, Partial<unknown>>()

function getCachedDefaults<T>(serde: UrlStateSerDe<T>): Partial<T> {
  if (!serde.defaults) return {} as Partial<T>
  if (serde.volatileDefaults) return serde.defaults()
  const cached = _defaultsCaches.get(serde.cacheKey)
  if (cached !== undefined) return cached as Partial<T>
  const fresh = serde.defaults()
  _defaultsCaches.set(serde.cacheKey, fresh as Partial<unknown>)
  return fresh
}

export function _resetAllDefaultsCachesForTesting(): void {
  _defaultsCaches.clear()
}

export function useUrlState<T>(serde: UrlStateSerDe<T>): {
  state: ComputedRef<T>
  update: (patch: Partial<T>) => void
} {
  const route = useRoute()
  const router = useRouter()

  const state = computed<T>(() => {
    const defaults = getCachedDefaults(serde)
    const merged: Record<string, unknown> = { ...defaults }
    for (const k in route.query) {
      const v = route.query[k]
      if (v !== undefined) merged[k] = v
    }
    return serde.parse(merged)
  })

  function update(patch: Partial<T>): void {
    const current = { ...route.query } as Record<string, string | undefined>
    const next = serde.serialize(patch, current)
    router.replace({ query: next })
  }

  return { state, update }
}
